/*
 * FICHIER: Logo.js
 * BUT: Composant réutilisable pour afficher le logo OffiSport
 * 
 * FONCTIONNALITÉS :
 * - Affichage du logo textuel "OffiSport" avec stylisation
 * - Navigation vers l'accueil quand cliqué
 * - Support de différents modes de rendu (lien, texte simple, div)
 * - Styles adaptatifs selon le contexte (header, footer)
 * - Responsive design
 * 
 * USAGE :
 * - <Logo /> : Avec lien vers l'accueil (utilisation normale)
 * - <Logo asText /> : Texte simple sans lien
 * - <Logo asDiv /> : Dans un div au lieu d'un lien
 * 
 * STYLES CONTEXTUELS :
 * - Header normal : Texte sombre
 * - Header scrollé : Texte sombre
 * - Footer : Texte blanc (auto-détecté via CSS)
 */

// IMPORTS REACT ET COMPOSANTS
import React from 'react';

// IMPORT STYLED COMPONENTS POUR LE STYLING
import styled from 'styled-components';

// IMPORT REACT ROUTER POUR LA NAVIGATION
import { Link } from 'react-router-dom';

/*
 * =================================================================================
 * STYLED COMPONENTS - Composants stylés pour le logo
 * =================================================================================
 */

// WRAPPER PRINCIPAL DU LOGO (AVEC LIEN)
const LogoWrapper = styled(Link)`
  text-decoration: none;              // Supprime le soulignement du lien
  display: flex;
  align-items: center;                // Centrage vertical du contenu
  padding: 5px;                       // Padding pour zone de clic plus large
`;

// CONTENEUR DU TEXTE DU LOGO (SANS LIEN)
const LogoTextContainer = styled.div`
  display: flex;
  align-items: center;                // Centrage vertical du contenu
  padding: 5px;                       // Padding cohérent avec LogoWrapper
`;

// TEXTE STYLÉ DU LOGO
const LogoText = styled.span`
  font-size: 24px;                    // Taille de base
  font-weight: 700;                   // Police grasse
  color: #232323;                     // Couleur sombre par défaut
  
  /* STYLE POUR LA PARTIE "SPORT" (span imbriqué) */
  span {
    color: #34A853;                   // Couleur verte pour "Sport"
    font-weight: 800;                 // Police extra-grasse pour contraste
  }

  /* STYLE SPÉCIFIQUE POUR LE FOOTER (fond sombre) */
  /* Le sélecteur "footer &" signifie "quand ce composant est dans un footer" */
  footer & {
    color: #ffffff;                   // Texte blanc sur fond sombre
    
    /* La partie "Sport" garde sa couleur verte même dans le footer */
    span {
      color: #34A853;
    }
  }

  /* STYLE SPÉCIFIQUE POUR LE HEADER QUAND IL EST SCROLLÉ */
  /* Le sélecteur ".scrolled &" signifie "quand dans un élément avec classe scrolled" */
  .scrolled & {
    color: #232323;                   // Texte sombre pour lisibilité
    
    span {
      color: #34A853;                 // "Sport" reste vert
    }
  }
`;

/*
 * =================================================================================
 * COMPOSANT PRINCIPAL LOGO
 * =================================================================================
 */

/**
 * Composant Logo - Affiche le logo OffiSport
 * 
 * PROPS :
 * @param {boolean} asText - Si true, affiche seulement le texte sans lien
 * @param {boolean} asDiv - Si true, utilise un div au lieu d'un Link
 * 
 * LOGIQUE :
 * - Par défaut : Logo cliquable avec lien vers l'accueil
 * - Mode asText : Texte simple pour usage décoratif
 * - Mode asDiv : Dans un conteneur div (pour cas spéciaux)
 * 
 * @returns {JSX.Element} Le composant logo rendu
 */
const Logo = ({ asText = false, asDiv = false }) => {
  // MODE TEXTE SIMPLE (sans lien ni interaction)
  // Utilisé quand on veut juste afficher le logo sans navigation
  if (asText) {
    return (
      <LogoTextContainer>
        <LogoText>
          Offi<span>Sport</span>        {/* "Offi" normal + "Sport" en vert */}
        </LogoText>
      </LogoTextContainer>
    );
  }
  
  // MODE DIV (avec div au lieu de Link)
  // Utilisé dans des cas spéciaux où un lien n'est pas approprié
  if (asDiv) {
    return (
      <LogoTextContainer>
        <LogoText>
          Offi<span>Sport</span>        {/* Structure identique au mode texte */}
        </LogoText>
      </LogoTextContainer>
    );
  }

  // MODE PAR DÉFAUT (avec lien vers l'accueil)
  // C'est l'usage normal du logo dans la navbar
  return (
    <LogoWrapper to="/">               {/* Lien React Router vers la page d'accueil */}
      <LogoText>
        Offi<span>Sport</span>          {/* Logo cliquable pour navigation */}
      </LogoText>
    </LogoWrapper>
  );
};

// Export par défaut pour utilisation dans d'autres composants
export default Logo; 